import { afterNextRender, Injectable, isDevMode, inject } from '@angular/core';
import {
  Faro,
  getWebInstrumentations,
  LogLevel,
  initializeFaro,
} from '@grafana/faro-web-sdk';
// import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { environment } from '../../../environments/environment';
import { CookieService } from './cookie.service';

interface UserDetails {
  id: string;
  email?: string;
  mobile?: string;
  name?: string;
  role?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FaroService {
  private faro!: Faro;
  private initialized = false;
  private currentUser: UserDetails | null = null;
  private cookieService = inject(CookieService);

  constructor() {
    afterNextRender(() => {
      this.initializeFaro();
    });
  }

  public initializeFaro() {
    if (this.initialized) {
      return;
    }

    const consent = this.cookieService.getCookie('cookie-consent');
    if (consent !== 'accepted') {
      console.log('Faro initialization skipped: Cookie consent not accepted');
      return;
    }

    const config = {
      app: {
        name: environment.grafana.appName || 'Learnbird Website',
        version: environment.version || '1.0.0',
        environment: isDevMode() ? 'development' : 'production',
      },
      url: environment.grafana?.url,
      instrumentations: [
        ...getWebInstrumentations(),
        // new TracingInstrumentation(), // Enable this to get end-to-end visibility for HTTP requests.
      ],
      debug: isDevMode(),
    };

    try {
      this.faro = initializeFaro(config);
      this.initialized = true;
      this.captureLog('Faro initialized successfully', LogLevel.INFO);
    } catch (error) {
      console.error('Failed to initialize Faro:', error);
    }
  }

  // Track user login
  trackUserLogin(user: UserDetails) {
    if (!this.initialized) {
      console.log('Faro not initialized: Cannot track user login');
      return;
    }

    this.currentUser = user;

    // Set user context for all future events
    this.faro.api.setUser({
      id: user.id,
      email: user.email || 'Unknown',
      attributes: {
        name: user.name || 'Unknown',
        mobile: user.mobile || 'Unknown',
        role: user.role || 'Unknown',
      },
    });

    // Log the login event
    this.captureEvent('user_login', {
      userId: user.id,
      userEmail: user.email || 'Unknown',
      userMobile: user.mobile || 'Unknown',
      userName: user.name || 'Unknown',
      userRole: user.role || 'Unknown',
    });

    this.captureLog(`User logged in: ${user.email}`, LogLevel.INFO);
  }

  // Track user logout
  trackUserLogout() {
    if (!this.initialized || !this.currentUser) {
      return;
    }

    // Log the logout event
    this.captureEvent('user_logout', {
      userId: this.currentUser.id,
      userEmail: this.currentUser.email || 'Unknown',
      userMobile: this.currentUser.mobile || 'Unknown',
      userName: this.currentUser.name || 'Unknown',
      userRole: this.currentUser.role || 'Unknown',
    });

    this.captureLog(
      `User logged out: ${this.currentUser.email}`,
      LogLevel.INFO
    );

    // Clear user context
    this.faro.api.resetUser();
    this.currentUser = null;
  }

  // Method to manually capture errors
  captureError(error: Error) {
    if (!this.initialized) {
      console.error('Faro not initialized:', error);
      return;
    }
    this.faro.api.pushError(error);
  }

  // Method to manually capture logs
  captureLog(message: string, level: LogLevel = LogLevel.INFO) {
    if (!this.initialized) {
      console.log('Faro not initialized:', message);
      return;
    }
    this.faro.api.pushLog([message], { level });
  }

  // Method to manually capture events
  captureEvent(name: string, attributes?: Record<string, string>) {
    if (!this.initialized) {
      console.log('Faro not initialized:', name, attributes);
      return;
    }
    this.faro.api.pushEvent(name, attributes);
  }
}
